import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Avatar, Box, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { identifiers } from 'utils/constants/identifiers';
import { fetchFromStorage } from 'utils/storage';
import { pageSubtitleSx, pageTitleSx } from 'utils/styles/pageHeader';
import strings from '../../../common/Translation/Translate';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import CheckInCheckout from '../../../components/CheckInCheckout';
import { getAvatarPalette } from '../../../utils/avatarColors';
import axiosInstance from '../../../utils/axios';
import { URLS } from '../../../utils/constants/urls';
import Navigation from '../../../utils/hooks/Navigation';
import { ChangePassword } from './ChangePassword';
import EditProfileDrawer from './EditProfile';

const formatRole = (role?: string | null) => {
  if (!role) return '—';
  return role.replace(/^ROLE_/, '').replace(/_/g, ' ');
};

const getInitials = (firstName?: string, lastName?: string) => {
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return initials || '?';
};

const InfoField = ({ label, value }: { label: string; value?: ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: 'text.secondary',
        lineHeight: 1.2,
        mb: 0.5,
      }}>
      {label}
    </Typography>
    {typeof value === 'string' || value == null ? (
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }}>
        {value || '—'}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

const profileCardSx = {
  height: '100%',
  p: 2,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
} as const;

const UserProfile: FC = () => {
  const initOrg: string | null = fetchFromStorage(identifiers.organization);
  const [userData, setUserData] = useState<any>(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const getUser = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.userProfile);
      setUserData(data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  }, []);

  useEffect(() => {
    getUser();
  }, [getUser]);

  if (!userData) return null;

  const { firstName, lastName, email, phone, role, city, state, country, address, pinCode, companyName } = userData;
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim() || '—';
  const palette = getAvatarPalette(fullName);
  const company = companyName?.trim() || '—';
  const location = [country?.label, state?.label, city?.label].filter(Boolean).join(', ') || '—';
  const fullAddress = [address, pinCode].filter(Boolean).join(', ') || '—';

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: 1.5,
        mb: 2,
        '&.MuiContainer-root': { pl: 0, pr: 0 },
      }}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item xs>
          <Typography sx={pageTitleSx}>{strings.updateProfile_title_text || 'User Profile'}</Typography>
          <Typography sx={{ ...pageSubtitleSx, mt: 0.25 }}>Your account details and attendance.</Typography>
        </Grid>
        {initOrg !== '*' && (
          <Grid item>
            <Navigation url="/" />
          </Grid>
        )}
      </Grid>

      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' },
          gridAutoRows: { md: '1fr' },
          gap: 1.25,
          alignItems: 'stretch',
        }}>
        <Box sx={profileCardSx}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: palette.bg,
                color: palette.color,
                fontWeight: 600,
                fontSize: '1.125rem',
              }}
              alt={fullName}>
              {getInitials(firstName, lastName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.3 }} noWrap>
                {fullName}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.4 }} noWrap>
                {email || '—'}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              mt: 1.75,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}>
            <InfoField
              label="Role"
              value={
                <Chip
                  size="small"
                  label={formatRole(role?.label || role?.value)}
                  sx={theme => ({
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    border: 'none',
                  })}
                />
              }
            />
            <InfoField label="Company" value={company} />
          </Box>

          <Box
            sx={{
              mt: 1.75,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}>
            <InfoField label="Phone" value={phone?.value} />
            <InfoField label="Location" value={location} />
          </Box>

          <Box sx={{ mt: 1.75 }}>
            <InfoField label="Address" value={fullAddress} />
          </Box>

          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 'auto', pt: 2 }}>
            <PrimaryButton
              onClick={() => setEditDrawerOpen(true)}
              fullWidth={false}
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              data-testid="UserProfile-btn-edit"
              sx={{ textTransform: 'none', minHeight: 36, px: 1.75, color: '#fff' }}>
              {strings.user_profile_edit_profile || 'Edit Profile'}
            </PrimaryButton>
            <ChangePassword username={email} />
          </Stack>
        </Box>

        <Box sx={{ minWidth: 0, height: '100%', display: 'flex' }}>
          <CheckInCheckout />
        </Box>
      </Box>

      <EditProfileDrawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        onSaved={() => {
          void getUser();
        }}
      />
    </Container>
  );
};

export default UserProfile;
